import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { services, problems, credentials, expertise } from '../mock';
import { Target, TrendingUp, ShoppingBag, Shield, ArrowRightCircle, Check, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Target,
  TrendingUp,
  ShoppingBag,
  Shield,
  ArrowRightCircle
};

export const Home = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    monthly_revenue: '',
    problem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToContact = () => {
    const element = document.getElementById('contact-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        monthly_revenue: formData.monthly_revenue,
        problem: formData.problem
      });

      toast({
        title: 'Application Submitted',
        description: 'Thank you! We will review your submission and get back to you soon.',
      });

      setFormData({
        name: '',
        email: '',
        monthly_revenue: '',
        problem: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Please try again or contact us directly via WhatsApp.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" style={{ color: '#1e1919' }}>
                Most businesses don't need more ideas.
              </h1>
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight" style={{ color: '#059669' }}>
                They need fewer mistakes.
              </h2>
              <p className="text-xl mb-8" style={{ color: '#736c64' }}>
                Execution-led business rebuilding by someone who has done business of €50m+ across the last two years, 
                worked with telcos directly, and scaled operations globally in circular economy and ITAD.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-lg px-8"
                style={{ backgroundColor: '#059669', color: 'white' }}
              >
                Work With Us
              </Button>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/187041/pexels-photo-187041.jpeg?w=800&q=80"
                alt="Business growth analytics on tablet"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Strip */}
      <section className="py-12 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {credentials.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold mb-2" style={{ color: '#059669' }}>
                  {item.stat}
                </div>
                <div className="text-sm" style={{ color: '#736c64' }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Fix */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#1e1919' }}>
              What We Fix
            </h2>
            <p className="text-lg" style={{ color: '#736c64' }}>
              Most businesses fail from poor execution, weak systems, bad marketplace strategy, 
              cashflow blindness, and operational rot. We specialize in circular economy, ITAD, 
              and tech resale operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {problems.map((item, index) => (
              <div
                key={index}
                className="p-6 border border-gray-200 rounded-lg"
                style={{ backgroundColor: '#f7f5f2' }}
              >
                <h3 className="text-xl font-semibold mb-2" style={{ color: '#1e1919' }}>
                  {item.problem}
                </h3>
                <p style={{ color: '#736c64' }}>
                  {item.solution}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#1e1919' }}>
              How We Help
            </h2>
            <p className="text-lg" style={{ color: '#736c64' }}>
              Not coaching. Not advice from slides. Operator-level intervention.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div key={service.id} className="bg-white p-8 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="p-3 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: '#f7f5f2' }}
                    >
                      <Icon className="h-6 w-6" style={{ color: '#059669' }} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e1919' }}>
                        {service.title}
                      </h3>
                      <p className="mb-4" style={{ color: '#736c64' }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="pl-16">
                    <p className="text-sm font-semibold mb-3" style={{ color: '#059669' }}>
                      Output:
                    </p>
                    <ul className="space-y-2">
                      {service.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#736c64' }}>
                          <Check className="h-4 w-4 flex-shrink-0" style={{ color: '#059669' }} />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#1e1919' }}>
                Who's Behind This
              </h2>
              <p className="text-lg mb-6" style={{ color: '#736c64' }}>
                Sean Gavin has done business of €50m+ across the last two years. 
                13+ years in the industry, from shop floor to technician to sales manager to top operator.
              </p>
              <p className="text-lg mb-6" style={{ color: '#736c64' }}>
                Strategic partner with Refurbed. Former top-tier Back Market seller who left due to platform fraud and commission issues. 
                Direct relationships with telcos, wholesale networks, and multi-channel software platforms. 
                Strong partnerships with DHL and UPS for shipping rates and insurance.
                Global sourcing across EU, USA, Dubai, Singapore, China, Japan, and Australia.
              </p>
              <p className="text-lg font-semibold" style={{ color: '#059669' }}>
                This is operator-level work. Not coaching.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e1919' }}>
                Direct Experience
              </h3>
              <ul className="space-y-3">
                {expertise.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                    <span style={{ color: '#736c64' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20" style={{ backgroundColor: '#1e1919' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-white">
                This Is For You If:
              </h2>
              <ul className="space-y-3">
                {[
                  'You are stuck at €10k to €200k/month and cannot scale cleanly',
                  'You are bleeding money from refunds, warranties, fraud, or inefficiency',
                  'You have capital but no execution clarity',
                  'Your business looks fine but feels chaotic underneath',
                  'You are starting from zero and need proper structure',
                  'You are transitioning from job to full-time founder',
                  'You are about to raise, expand, or pivot'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                    <span className="text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-white">
                This Is NOT For:
              </h2>
              <ul className="space-y-3">
                {[
                  'Wantrepreneurs',
                  'People looking for motivation',
                  'Broke founders with no leverage',
                  'Anyone wanting shortcuts without effort'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="h-5 w-5 flex-shrink-0 mt-0.5 flex items-center justify-center">
                      <div className="h-0.5 w-3" style={{ backgroundColor: '#059669' }}></div>
                    </div>
                    <span className="text-gray-400">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Apply Section */}
      <section id="contact-section" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#1e1919' }}>
              Work With Us
            </h2>
            <p className="text-lg" style={{ color: '#736c64' }}>
              Retained advisory. Short sprints. Project-based execution.
              <br />
              No hourly billing. No endless calls. Clear outcomes only.
            </p>
          </div>
          <div className="bg-white p-8 rounded-lg border border-gray-200">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e1919' }}>
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#059669' }}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e1919' }}>
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#059669' }}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e1919' }}>
                  Current Revenue (Monthly)
                </label>
                <input
                  type="text"
                  name="monthly_revenue"
                  value={formData.monthly_revenue}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#059669' }}
                  placeholder="€10k, €50k, €200k, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1e1919' }}>
                  What's Actually Broken?
                </label>
                <textarea
                  name="problem"
                  value={formData.problem}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#059669' }}
                  placeholder="Be specific. What's not working?"
                ></textarea>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full text-lg"
                style={{ backgroundColor: '#059669', color: 'white' }}
              >
                {isSubmitting ? 'Sending...' : 'Apply to Work Together'}
              </Button>
            </form>
            
            {/* WhatsApp Contact */}
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm mb-3" style={{ color: '#736c64' }}>
                Or contact us directly
              </p>
              <Button
                onClick={() => window.open('https://wa.me/353830450305?text=Hi, I would like to discuss working with Re-Surge', '_blank')}
                variant="outline"
                size="lg"
                className="w-full text-lg"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};